import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { GraphqlService } from '../../core/graphql/graphql.service';

interface NavItem {
  label: string;
  route?: string;
  svgPath: string;
  action?: () => void;
  children?: NavItem[];
  roles?: string[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  host: { class: 'flex shrink-0' },
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(MsalService);
  private gql = inject(GraphqlService);

  private expandedLabels = signal<string[]>([]);
  private userRole = signal<string>('');

  ngOnInit() {
    const account =
      this.authService.instance.getActiveAccount() ??
      this.authService.instance.getAllAccounts()[0];
    if (account) {
      this.gql.verifyUser(account.username).subscribe({
        next: user => this.userRole.set(user?.role?.toLowerCase() ?? ''),
        error: () => this.userRole.set(''),
      });
    }
  }

  readonly topItems: NavItem[] = [
    {
      label: 'Cases',
      svgPath:
        'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
      children: [
        {
          label: 'List',
          route: '/cases',
          svgPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
        },
        {
          label: 'Download',
          route: '/case-history',
          svgPath: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
        },
      ],
    },
    {
      label: 'Users',
      route: '/users',
      svgPath:
        'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
      roles: ['Admin'],
    },
  ];

  readonly bottomItems: NavItem[] = [
    {
      label: 'Settings',
      svgPath:
        'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      children: [
        {
          label: 'Countries',
          route: '/countries',
          svgPath: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
          roles: ['Admin'],
        },
        {
          label: 'Content Templates',
          route: '/content-templates',
          svgPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          roles: ['Admin'],
        },
        {
          label: 'Dynamic Forms',
          route: '/forms',
          svgPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          roles: ['Admin'],
        },
        {
          label: 'GraphQL Explorer',
          route: '/graphql-explorer',
          svgPath: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
          roles: ['Admin'],
        },
        {
          label: 'Hangfire',
          route: '/hangfire',
          svgPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
          roles: ['Admin'],
        },
        {
          label: 'Upload Chrome Extension',
          route: '/extension-upload',
          svgPath: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z',
          roles: ['Admin'],
        },
      ],
    },
  ];

  isVisible(item: NavItem): boolean {
    if (!item.roles) return true;
    return item.roles.map(r => r.toLowerCase()).includes(this.userRole());
  }

  visibleChildren(item: NavItem): NavItem[] {
    return (item.children ?? []).filter(child => this.isVisible(child));
  }

  isActive(item: NavItem): boolean {
    return !!item.route && this.router.url.startsWith(item.route);
  }

  hasActiveChild(item: NavItem): boolean {
    return !!item.children?.some(child => this.isActive(child));
  }

  isExpanded(item: NavItem): boolean {
    return this.expandedLabels().includes(item.label) || this.hasActiveChild(item);
  }

  toggleItem(item: NavItem): void {
    const labels = this.expandedLabels();
    if (this.isExpanded(item) && !this.hasActiveChild(item)) {
      this.expandedLabels.set(labels.filter(l => l !== item.label));
    } else {
      this.expandedLabels.set([...labels.filter(l => l !== item.label), item.label]);
    }
  }

  navigate(item: NavItem): void {
    if (item.route) {
      this.router.navigateByUrl(item.route);
    } else if (item.action) {
      item.action();
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }
}
